import { useMemo, useState } from 'react'
import { useAuth } from '@/lib/AuthContext'
import {
  useBoardSuggestions,
  useDeleteOwnSuggestion,
  useIdeaBoardVoting,
  useIdeaBoardCategoryNames,
  useIdeaBoardAttachments,
} from '@/idea-board/hooks/useIdeaBoardData'
import SuggestionCard from '@/idea-board/components/SuggestionCard'
import SubmitIdeaDialog from '@/idea-board/components/SubmitIdeaDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, TrendingUp, Clock, Search, Lightbulb } from 'lucide-react'

export default function IdeaBoardTab() {
  const { user } = useAuth()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [sortBy, setSortBy] = useState('score')
  const [filterCat, setFilterCat] = useState('All')
  const [search, setSearch] = useState('')

  const { data: suggestions = [], isLoading } = useBoardSuggestions()
  const suggestionIds = useMemo(() => suggestions.map((s) => s.id), [suggestions])
  const { data: allAttachments = [] } = useIdeaBoardAttachments(suggestionIds)
  const attachmentsBySuggestion = useMemo(() => {
    const map = {}
    allAttachments.forEach((a) => {
      if (!map[a.suggestion_id]) map[a.suggestion_id] = []
      map[a.suggestion_id].push(a)
    })
    return map
  }, [allAttachments])
  const categoryNames = useIdeaBoardCategoryNames()
  const categoryFilterOptions = useMemo(() => ['All', ...categoryNames], [categoryNames])
  const { scores, userVotes, handleVote } = useIdeaBoardVoting(user?.id)
  const deleteMutation = useDeleteOwnSuggestion()

  const filtered = useMemo(() => {
    let items =
      filterCat === 'All' ? suggestions : suggestions.filter((s) => s.category === filterCat)

    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(
        (s) =>
          s.title.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q),
      )
    }

    if (sortBy === 'score') {
      items = [...items].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0))
    }
    return items
  }, [suggestions, filterCat, sortBy, scores, search])

  return (
    <>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-2xl font-bold text-foreground tracking-tight">Innovation Hub</h2>
          <p className="text-muted-foreground text-sm mt-0.5">
            Share process improvements and automation ideas — vote on what matters most.
          </p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Submit Idea
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-5">
        <button
          type="button"
          onClick={() => setSortBy('score')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
            sortBy === 'score'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground bg-white border border-border'
          }`}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          Top Voted
        </button>
        <button
          type="button"
          onClick={() => setSortBy('newest')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
            sortBy === 'newest'
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground bg-white border border-border'
          }`}
        >
          <Clock className="w-3.5 h-3.5" />
          Newest
        </button>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search ideas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm border-border bg-transparent"
          />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-44 h-8 text-sm">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {categoryFilterOptions.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(filterCat !== 'All' || search) && (
          <button
            type="button"
            onClick={() => {
              setFilterCat('All')
              setSearch('')
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Clear
          </button>
        )}
        <div className="ml-auto text-xs text-muted-foreground">
          {filtered.length} idea{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array(4)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-border">
          <Lightbulb className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">No ideas found.</p>
          <p className="text-sm text-muted-foreground mt-1">
            Try adjusting filters or submit the first idea!
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              score={scores[s.id] || 0}
              userVote={userVotes[s.id]?.value || 0}
              onVote={handleVote}
              canDelete={user?.id && s.created_by === user.id}
              onDelete={(id) => deleteMutation.mutate(id)}
              deleting={deleteMutation.isPending}
              attachments={attachmentsBySuggestion[s.id] || []}
            />
          ))}
        </div>
      )}

      <SubmitIdeaDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  )
}
